import { useEffect } from "react";

// Globals the vendored Exponea snippet reads as bare identifiers. Set here at
// injection; declared so `inject()` can assign them without a type assertion.
declare global {
  interface Window {
    bloomreachEventTarget?: string;
    bloomreachUserId?: string;
  }
}

// Upper bound on the idle wait, used for BOTH the `requestIdleCallback` timeout
// and the no-rIC (older Safari) `setTimeout` fallback. Injection eventually
// fires even on a busy thread, but deferred enough to clear most of the
// SFCC-11801 hydration wedge window rather than being force-injected into it.
const IDLE_INJECT_TIMEOUT_MS = 5000;

export interface BloomreachConfig {
  enabled: boolean;
  eventTarget: string;
  scriptPath: string;
}

interface BloomreachScriptsProps {
  config?: BloomreachConfig;
  /** Customer number for logged-in shoppers; empty string for guests. */
  userId: string;
  nonce?: string;
}

/**
 * Bloomreach (Exponea) client-side behavioural tracking. Renders nothing when
 * disabled (WDH). Injected client-side after `load` + on idle (NOT at parse):
 * keeps the Exponea SDK eval + its dead ~5.5s `window.ga` poll (legacy UA
 * bridge; this site is GA4-only so `window.ga` never appears) off the hydration
 * window, where it was a rapid-click-wedge contributor (SFCC-11801). Sets the
 * `bloomreachEventTarget`/`bloomreachUserId` globals the vendored snippet reads,
 * just before injection. Mirrors `ZendeskSnippetScript`. SFCC-11019.
 *
 * ============================ KNOWN BUG ============================
 * Identity is set ONCE, at injection. A same-session guest->login
 * (account-drawer sign-in revalidates root CLIENT-SIDE, no reload) updates the
 * `userId` prop, but the re-fired effect hits the `#bloomreach-snippet` dedup,
 * so Exponea keeps the GUEST identity for the rest of the session. No
 * `exponea.identify()` re-identify path exists. PRE-EXISTING (the old inline
 * script never re-executed on a React update either), not introduced by the
 * defer. Fix = an `exponea.identify()` call with a project-specific payload
 * (profile-merge risk) => martech-owned, needs its own ticket.
 * ===================================================================
 */
export function BloomreachScripts({
  config,
  userId,
  nonce,
}: BloomreachScriptsProps) {
  const enabled = config?.enabled ?? false;
  const eventTarget = config?.eventTarget;
  const scriptPath = config?.scriptPath;

  useEffect(() => {
    if (!enabled || !scriptPath || eventTarget === undefined) return;
    if (document.getElementById("bloomreach-snippet")) return;

    let cancelled = false;
    let idleHandle: number | undefined;

    const inject = () => {
      if (cancelled || document.getElementById("bloomreach-snippet")) return;
      // Snippet reads these as bare globals; set before injecting.
      window.bloomreachEventTarget = eventTarget;
      window.bloomreachUserId = userId; // set once only; see KNOWN BUG above

      const script = document.createElement("script");
      script.id = "bloomreach-snippet";
      script.src = scriptPath;
      // async, not defer: an appended script runs async-when-fetched regardless;
      // `defer` only affects parser-inserted scripts. Matches ZendeskSnippetScript.
      script.async = true;
      if (nonce) script.nonce = nonce;
      document.head.appendChild(script);
    };

    // Wait for idle after load so the SDK exec + GA poll stay off the hydration
    // window; `timeout` bounds the wait on a busy thread.
    const schedule = () => {
      if (cancelled) return;
      if (typeof window.requestIdleCallback === "function") {
        idleHandle = window.requestIdleCallback(inject, {
          timeout: IDLE_INJECT_TIMEOUT_MS,
        });
      } else {
        idleHandle = window.setTimeout(inject, IDLE_INJECT_TIMEOUT_MS);
      }
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener("load", schedule);
      if (idleHandle !== undefined) {
        if (typeof window.cancelIdleCallback === "function") {
          window.cancelIdleCallback(idleHandle);
        } else {
          window.clearTimeout(idleHandle);
        }
      }
    };
  }, [enabled, scriptPath, eventTarget, userId, nonce]);

  return null;
}
